<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { isNullish, TuButton } from "@tinyurldev/tu-ui-vue";

import { useTranslation } from "@Resources/js/composables";
import { XAuthStore } from "@Modules/users/Store/XAuthStore";
import type { BrandedDomainCheckResult } from "@Modules/teams/Api/DomainsApi";

//
// Setup
const authStore = XAuthStore();
const { __ } = useTranslation();

//
// Props
interface Props {
  domain?: string | null;
  personalDomain?: string;
  checkSubDomain: (domain: string) => Promise<BrandedDomainCheckResult>;
}

const props = defineProps<Props>();

//
// Data
const busy = ref(false);
const domainIsActive = ref<boolean | null>(null);
const domainCheckMessage = ref<string | null>(null);

//
// Computed
const subdomain = computed<string>(() => {
  if (!props.domain) {
    return "";
  }

  const parts = props.domain.split(".");
  return parts.slice(0, parts.length - 2).join(".");
});

//
// Methods
async function check(): Promise<void> {
  if (!props.domain) {
    return;
  }

  busy.value = true;
  domainCheckMessage.value = null;

  try {
    const result = await props.checkSubDomain(props.domain);
    domainIsActive.value = result.active;
    domainCheckMessage.value = result.reason;
  } catch {
    domainCheckMessage.value = __("organization_domains_settings.check_added_subdomain.check_error");
  } finally {
    busy.value = false;
  }
}

//
// Watchers
watch(() => props.domain, () => {
  domainIsActive.value = null;
  domainCheckMessage.value = null;
});

watch(domainIsActive, (newValue, oldValue) => {
  if (oldValue !== null && oldValue !== newValue) {
    authStore.refreshApplicationState();
  }
});

//
// Exposed Test Vars
defineExpose({ busy, domainIsActive, domainCheckMessage, check });
</script>

<template>
  <div class="check-subdomain-added">
    <h4 class="check-subdomain-added__title">
      <template v-if="!isNullish(domainIsActive)">
        <i :class="`fa ${domainIsActive ? 'fa-check text-success' : 'fa-times text-danger'}`" />
      </template>

      {{ __("organization_domains_settings.check_added_subdomain.dns_setup_title") }}
    </h4>

    <p class="check-subdomain-added__description">
      {{ __("organization_domains_settings.check_added_subdomain.cname_description") }}
    </p>

    <h5 class="check-subdomain-added__cname-title">
      {{ __("organization_domains_settings.check_added_subdomain.cname_settings_title") }}
    </h5>

    <div class="check-subdomain-added__cname-record">
      <div class="check-subdomain-added__cname-field">
        <p class="check-subdomain-added__cname-label">
          {{ __("organization_domains_settings.check_added_subdomain.cname_name_label") }}
        </p>
        <p class="check-subdomain-added__cname-value _cname-name">
          {{ subdomain }}
        </p>
      </div>
      <div class="check-subdomain-added__cname-field">
        <p class="check-subdomain-added__cname-label">
          {{ __("organization_domains_settings.check_added_subdomain.cname_type_label") }}
        </p>
        <p class="check-subdomain-added__cname-value">
          CNAME
        </p>
      </div>
      <div class="check-subdomain-added__cname-field">
        <p class="check-subdomain-added__cname-label">
          {{ __("organization_domains_settings.check_added_subdomain.cname_ttl_label") }}
        </p>
        <p class="check-subdomain-added__cname-value">
          {{ __("organization_domains_settings.check_added_subdomain.cname_ttl_value") }}
        </p>
      </div>
      <div class="check-subdomain-added__cname-field">
        <p class="check-subdomain-added__cname-label">
          {{ __("organization_domains_settings.check_added_subdomain.cname_value_label") }}
        </p>
        <p class="check-subdomain-added__cname-value _cname-target">
          {{ personalDomain }}
        </p>
      </div>
    </div>

    <p
      v-if="!domainIsActive"
      class="check-subdomain-added__propagation-note"
    >
      {{ __("organization_domains_settings.check_added_subdomain.propagation_note") }}
    </p>

    <div
      v-if="domainCheckMessage"
      class="check-subdomain-added__alert check-subdomain-added__alert--error"
    >
      {{ domainCheckMessage }}
    </div>

    <div
      v-else-if="domainIsActive"
      class="check-subdomain-added__alert check-subdomain-added__alert--success"
    >
      {{ __("organization_domains_settings.check_added_subdomain.success_message") }}
    </div>

    <TuButton
      class="_check"
      type="button"
      is-block
      :label="__('organization_domains_settings.check_added_subdomain.check_button')"
      :is-loading="busy"
      @click.stop="check"
    />
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;

.check-subdomain-added {
  display: flex;
  flex-flow: column;
  gap: spacing.$xxs;

  &__cname-record {
    display: flex;
    flex-flow: column;
    gap: spacing.$xxxs;
    padding: spacing.$xxs;
    border: border.$primary;
    border-radius: border.$medium-radius;
    background-color: color.$background-primary;
  }

  &__cname-field {
    display: flex;
    flex-flow: column;
    gap: spacing.$xxxs;
  }

  &__cname-label {
    font-weight: bold;
  }

  &__alert {
    padding: spacing.$xxs;
    border-radius: border.$medium-radius;

    &--error {
      background-color: color.$alert-light;
      color: color.$alert-dark;
    }

    &--success {
      background-color: color.$secondary-light;
      color: color.$secondary-dark;
    }
  }

  &__title,
  &__cname-title,
  &__cname-label,
  &__cname-value,
  &__description,
  &__propagation-note {
    margin: 0;
  }
}
</style>
